// Vault deposit hook — initializes a Paystack transaction and opens
// Paystack Inline checkout for reserve top-ups.
//
// Flow:
//   1. User picks wallet (betting/investment) + amount
//   2. Frontend calls /api/vault/initialize-deposit
//   3. API returns Paystack access_code + authorization_url
//   4. Frontend opens Paystack Inline popup (or redirects)
//   5. Paystack sends charge.success webhook → RPC credits wallet
//   6. Frontend polls/listens for balance update

import { useCallback, useMemo, useState } from "react";
import { useAuthSession } from "../auth/useAuthSession.ts";

type WalletType = "betting" | "investment";

export interface DepositState {
  /** True while initializing with Paystack */
  loading: boolean;
  error: string | null;
  /** Start a deposit flow */
  initDeposit: (walletType: WalletType, amountGhs: number) => Promise<void>;
  /** Clear error state */
  clearError: () => void;
}

declare global {
  interface Window {
    PaystackPop?: {
      setup: (config: {
        key: string;
        email: string;
        amount: number;
        currency: string;
        ref: string;
        metadata: Record<string, unknown>;
        channels: string[];
        onClose: () => void;
        callback: (response: { reference: string; status: string }) => void;
      }) => { openIframe: () => void };
    };
  }
}

const PAYSTACK_PUBLIC_KEY =
  (typeof import.meta !== "undefined" ? (import.meta as unknown as { env?: Record<string, string> }).env?.VITE_PAYSTACK_PUBLIC_KEY : undefined) ??
  "";

export function useVaultDeposit(onSuccess?: () => void): DepositState {
  const { session } = useAuthSession();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clearError = useCallback(() => setError(null), []);

  const initDeposit = useCallback(
    async (walletType: WalletType, amountGhs: number) => {
      if (!session?.access_token) {
        setError("Please sign in to make a deposit");
        return;
      }

      if (amountGhs < 10) {
        setError("Minimum deposit is GH₵10");
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const resp = await fetch("/api/vault/initialize-deposit", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ wallet_type: walletType, amount: amountGhs }),
        });

        const result = (await resp.json()) as {
          authorization_url?: string;
          access_code?: string;
          reference?: string;
          error?: string;
        };

        if (!resp.ok || !result.authorization_url) {
          setError(result.error ?? "Failed to initialize deposit");
          setLoading(false);
          return;
        }

        // Try Paystack Inline (popup) first, fall back to redirect
        if (window.PaystackPop && PAYSTACK_PUBLIC_KEY && session.user?.email) {
          const handler = window.PaystackPop.setup({
            key: PAYSTACK_PUBLIC_KEY,
            email: session.user.email,
            amount: Math.round(amountGhs * 100),
            currency: "GHS",
            ref: result.reference!,
            metadata: {
              txn_type: "reserve_topup",
              user_id: session.user.id,
              wallet_type: walletType,
            },
            channels: ["mobile_money", "card", "bank"],
            onClose: () => {
              setLoading(false);
            },
            callback: (_response) => {
              setLoading(false);
              onSuccess?.();
            },
          });
          handler.openIframe();
        } else {
          // Redirect to Paystack checkout page
          window.location.href = result.authorization_url;
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "Network error");
        setLoading(false);
      }
    },
    [session, onSuccess],
  );

  return useMemo(
    () => ({ loading, error, initDeposit, clearError }),
    [loading, error, initDeposit, clearError],
  );
}
